import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  Chip,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Badge,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  AssignmentInd as AssignIcon,
  Replay as ReopenIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { messagingService } from '../../services/messaging.service';
import { useAuth } from '../../context/AuthContext';
import { theme } from '../../theme';
import { useMessagingWebSocket } from '../../hooks/useMessagingWebSocket';
import { superadminService } from '../../services/superadmin.service';

const STATUS_COLORS = {
  open: 'warning',
  assigned: 'info',
  closed: 'default',
};

const PRIORITY_COLORS = {
  low: 'success',
  medium: 'warning',
  high: 'error',
  urgent: 'error',
};

const STATUS_LABELS = {
  open: 'Ouverte',
  assigned: 'Attribuée',
  closed: 'Fermée',
};

const PRIORITY_LABELS = {
  low: 'Basse',
  medium: 'Moyenne',
  high: 'Haute',
  urgent: 'Urgente',
};

const MessagingPanel = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState('');
  const messagesEndRef = useRef(null);

  const joinConversationRef = useRef(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isSuperAdmin = user?.role === 'superadmin';

  const handleWsMessage = useCallback((data) => {
    if (!data) return;

    if (data.type === 'new_message' && data.message) {
      const message = data.message;

      setConversations((prev) => {
        const next = [...prev];
        const idx = next.findIndex((c) => c.id === message.conversation_id);
        if (idx === -1) return prev;

        next[idx] = {
          ...next[idx],
          last_message: message,
        };

        return next;
      });

      if (message.conversation_id === selectedConversation?.id) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === message.id)) return prev;
          return [...prev, message];
        });
      }

      return;
    }

    if (data.type === 'new_conversation') {
      fetchConversations();
      fetchStats();
    }
  }, [selectedConversation?.id, statusFilter, searchQuery]);

  const { status: wsStatus, sendJson } = useMessagingWebSocket({
    enabled: !!user?.token,
    token: user?.token,
    onMessage: handleWsMessage,
    onOpen: () => {
      if (typeof joinConversationRef.current === 'function') {
        joinConversationRef.current();
      }
    },
  });

  useEffect(() => {
    fetchConversations();
    fetchStats();
    if (isAdmin) {
      fetchAdmins();
    }
  }, [statusFilter]);

  useEffect(() => {
    if (!assignDialogOpen) return;
    if (!user?.id) return;
    if (user?.role === 'admin') {
      setSelectedAdmin(user.id);
    }
  }, [assignDialogOpen, user?.id, user?.role]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
      markMessagesAsRead(selectedConversation.id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    if (!selectedConversation?.id) {
      joinConversationRef.current = null;
      return;
    }

    joinConversationRef.current = () => {
      sendJson({
        action: 'join_conversation',
        conversation_id: selectedConversation.id,
      });
    };

    if (wsStatus === 'open') {
      joinConversationRef.current();
    }
  }, [selectedConversation?.id, sendJson, wsStatus]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      const response = await messagingService.listConversations(params);
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await messagingService.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchAdmins = async () => {
    try {
      if (user?.role !== 'superadmin') {
        setAdmins([]);
        return;
      }

      const res = await superadminService.listAdminUsers({ page: 1, page_size: 200 });
      const data = res?.data;
      const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];

      const filtered = results
        .filter((u) => u && (u.role === 'admin' || u.role === 'superadmin'))
        .filter((u) => u.id !== user?.id);

      setAdmins(filtered);
    } catch (error) {
      console.error('Error fetching admins:', error);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await messagingService.retrieveConversation(conversationId);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const markMessagesAsRead = async (conversationId) => {
    try {
      await messagingService.markAllMessagesAsRead(conversationId);
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    const content = newMessage.trim();
    setNewMessage('');

    try {
      const sent = sendJson({
        action: 'send_message',
        conversation_id: selectedConversation.id,
        content,
      });

      if (!sent) {
        await messagingService.sendMessage(selectedConversation.id, content);
        fetchMessages(selectedConversation.id);
        fetchConversations();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setNewMessage(content);
    }
  };

  const handleAssignConversation = async () => {
    if (!selectedAdmin || !selectedConversation) return;

    try {
      await messagingService.assignConversation(selectedConversation.id, selectedAdmin);
      setAssignDialogOpen(false);
      fetchConversations();
      fetchStats();
      if (selectedConversation.id) {
        const updated = await messagingService.retrieveConversation(selectedConversation.id);
        setSelectedConversation(updated.data);
      }
    } catch (error) {
      console.error('Error assigning conversation:', error);
    }
  };

  const handleCloseConversation = async () => {
    if (!selectedConversation) return;

    try {
      await messagingService.closeConversation(selectedConversation.id);
      setCloseDialogOpen(false);
      fetchConversations();
      fetchStats();
      setSelectedConversation(null);
    } catch (error) {
      console.error('Error closing conversation:', error);
    }
  };

  const handleReopenConversation = async (conversationId) => {
    try {
      await messagingService.reopenConversation(conversationId);
      fetchConversations();
      fetchStats();
    } catch (error) {
      console.error('Error reopening conversation:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.started_by?.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.started_by?.last_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 200px)', gap: 2 }}>
      {/* Conversations List */}
      <Paper sx={{ width: 350, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.colors.border.light}` }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Conversations
          </Typography>
          
          {/* Stats */}
          {stats && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip 
                size="small" 
                label={`${stats.open} ouvertes`} 
                color="warning" 
                variant="outlined"
              />
              <Chip 
                size="small" 
                label={`${stats.assigned} attribuées`} 
                color="info" 
                variant="outlined"
              />
              <Chip 
                size="small" 
                label={`${stats.closed} fermées`} 
                color="default" 
                variant="outlined"
              />
            </Box>
          )}

          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Rechercher..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
            }}
          />

          {/* Filter Tabs */}
          <Tabs
            value={statusFilter}
            onChange={(e, v) => setStatusFilter(v)}
            sx={{ mt: 2 }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Toutes" value="all" />
            <Tab label="Ouvertes" value="open" />
            <Tab label="Attribuées" value="assigned" />
            <Tab label="Fermées" value="closed" />
          </Tabs>
        </Box>

        <List sx={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredConversations.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                Aucune conversation
              </Typography>
            </Box>
          ) : (
            filteredConversations.map((conversation) => (
              <ListItem
                key={conversation.id}
                button
                selected={selectedConversation?.id === conversation.id}
                onClick={() => setSelectedConversation(conversation)}
                sx={{
                  borderBottom: `1px solid ${theme.colors.border.light}`,
                  '&.Mui-selected': {
                    backgroundColor: theme.colors.primary[50],
                  },
                }}
              >
                <ListItemAvatar>
                  <Badge
                    badgeContent={conversation.unread_count}
                    color="error"
                    invisible={conversation.unread_count === 0}
                  >
                    <Avatar sx={{ bgcolor: theme.colors.primary[500] }}>
                      {conversation.started_by?.first_name?.[0] || 'U'}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                        {conversation.subject}
                      </Typography>
                      <Chip
                        size="small"
                        label={STATUS_LABELS[conversation.status]}
                        color={STATUS_COLORS[conversation.status]}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {conversation.last_message?.content || 'Aucun message'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {conversation.started_by?.first_name} {conversation.started_by?.last_name}
                        {conversation.assigned_to && ` • Assignée à ${conversation.assigned_to.first_name}`}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))
          )}
        </List>
      </Paper>

      {/* Chat Area */}
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {selectedConversation ? (
          <>
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: `1px solid ${theme.colors.border.light}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {selectedConversation.subject}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Initiée par {selectedConversation.started_by?.first_name} {selectedConversation.started_by?.last_name}
                    </Typography>
                    <Chip
                      size="small"
                      label={PRIORITY_LABELS[selectedConversation.priority]}
                      color={PRIORITY_COLORS[selectedConversation.priority]}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                    {selectedConversation.salon && (
                      <Chip
                        size="small"
                        label={selectedConversation.salon.name}
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                </Box>
                
                {isAdmin && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {selectedConversation.status !== 'closed' && (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AssignIcon />}
                          onClick={() => setAssignDialogOpen(true)}
                        >
                          {selectedConversation.assigned_to ? 'Réassigner' : 'Assigner'}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<CloseIcon />}
                          onClick={() => setCloseDialogOpen(true)}
                        >
                          Fermer
                        </Button>
                      </>
                    )}
                    {selectedConversation.status === 'closed' && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ReopenIcon />}
                        onClick={() => handleReopenConversation(selectedConversation.id)}
                      >
                        Rouvrir
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
              
              {selectedConversation.assigned_to && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Conversation assignée à {selectedConversation.assigned_to.first_name} {selectedConversation.assigned_to.last_name}
                </Alert>
              )}
            </Box>

            {/* Messages */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: theme.colors.grey[50] }}>
              {messages.map((message) => {
                const isOwn = message.sender?.id === user?.id;
                return (
                  <Box
                    key={message.id}
                    sx={{
                      display: 'flex',
                      justifyContent: isOwn ? 'flex-end' : 'flex-start',
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: '70%',
                        display: 'flex',
                        flexDirection: isOwn ? 'row-reverse' : 'row',
                        alignItems: 'flex-start',
                        gap: 1,
                      }}
                    >
                      <Avatar sx={{ width: 32, height: 32, bgcolor: isOwn ? theme.colors.primary[500] : theme.colors.grey[400] }}>
                        {message.sender?.first_name?.[0] || 'U'}
                      </Avatar>
                      <Box>
                        <Paper
                          sx={{
                            p: 2,
                            bgcolor: isOwn ? theme.colors.primary[500] : '#fff',
                            color: isOwn ? '#fff' : 'text.primary',
                            borderRadius: 2,
                          }}
                        >
                          <Typography variant="body1">{message.content}</Typography>
                        </Paper>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          {message.sender?.first_name} {message.sender?.last_name} • {new Date(message.created_at).toLocaleString()}
                          {message.is_read && isOwn && ' • Lu'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
              <div ref={messagesEndRef} />
            </Box>

            {/* Input */}
            {selectedConversation.status !== 'closed' && (
              <Box sx={{ p: 2, borderTop: `1px solid ${theme.colors.border.light}` }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    placeholder="Écrivez votre message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={selectedConversation.status === 'closed'}
                  />
                  <Button
                    variant="contained"
                    endIcon={<SendIcon />}
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || selectedConversation.status === 'closed'}
                  >
                    Envoyer
                  </Button>
                </Box>
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography color="text.secondary">
              Sélectionnez une conversation pour voir les messages
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)}>
        <DialogTitle>Assigner la conversation</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Assigner à</InputLabel>
            <Select
              value={selectedAdmin}
              onChange={(e) => setSelectedAdmin(e.target.value)}
              label="Assigner à"
            >
              <MenuItem value={user?.id}>Moi ({user?.first_name} {user?.last_name})</MenuItem>
              {isSuperAdmin && admins.map((admin) => (
                <MenuItem key={admin.id} value={admin.id}>
                  {admin.first_name} {admin.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleAssignConversation} variant="contained">
            Assigner
          </Button>
        </DialogActions>
      </Dialog>

      {/* Close Dialog */}
      <Dialog open={closeDialogOpen} onClose={() => setCloseDialogOpen(false)}>
        <DialogTitle>Fermer la conversation</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir fermer cette conversation ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleCloseConversation} variant="contained" color="error">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MessagingPanel;
